import React, { useEffect, useMemo, useRef, useState } from "react";
import { StoreInterface } from "@/assets/interfaces";
import CurrencySwitcher from "@/components/ui/currency-switcher";
import CartContext from "@/contexts/cart-context";
import AppSearchBar from "@/components/storefronts-v2/ui/store-seach-bar";
import { useSidemenu } from "@/components/hooks/useSidemenu";
import { areModalsShown } from "@/assets/js/utils/functions";
import { checkStoreIsOpen, getWhatsappLink } from "@/assets/js/utils/utils";
import { ModalsState } from "@/components/hooks/useModals";
import StoreLogo from "@/components/ui/store-logo";
import classNames from "classnames";
import MiniSearch from "./mini-search";
import { useRouter } from "next/router";
import { socialsDataMap } from "@/components/storefronts-v1/modals/details";
import StorefrontContext, { VisitRoute } from "@/contexts/storefront";
import StoreSideNav from "./sidenav";
import CartContextV2 from "@/contexts/cart-context-v2";

interface Props {
  store: StoreInterface;
  resetPage: () => void;
  toggleSearch?: () => void;
  modals: ModalsState<
    | "about_store"
    | "delivery_fees"
    | "refund_policy"
    | "more_info"
    | "image_details"
    | "faq"
    | "categories"
    | "about_images"
  >;
  toggle: (key: string) => void;
  mode?: "light" | "dark";
  openItem: (item: any) => void;
  allowSearch?: boolean;
  showWelcomeMessage?: boolean;
  showStoreMessage?: boolean;
}

const NewStoreHeader: React.FC<Props> = ({
  store,
  resetPage,
  mode,
  modals,
  toggle,
  openItem,
  allowSearch = true,
  showWelcomeMessage = true,
  showStoreMessage = true,
}) => {
  const router = useRouter();
  const { categories, logo, hero_image: heroImage, name: storeName, has_paid_subscription } = store;
  const { currencies, changeCurrency, formatAsCurrency, cart, toggleModal } = CartContextV2.useContainer();
  const { visitRoute, getPaths } = StorefrontContext.useContainer();

  const [fullViewOnMobile, setFullViewOnMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const stickyNavRef = useRef<HTMLDivElement>(null);

  const computedPaths = useMemo(() => getPaths(store), [store]);

  const { sidemenus, toggleSidemenu, closeAllSidemenus } = useSidemenu(["sidebar"]);
  const storeIs = checkStoreIsOpen(store);

  useEffect(() => {
    // Ensure body overflow-x is visible
    document.body.style.overflowX = "visible";

    const handleScroll = () => {
      if (window.scrollY > 70) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      // Clean up: Reset to default when the component unmounts
      document.body.style.overflowX = "";
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const getStoreIsClosedMsgToDisplay = () => {
    const checkBack = "We're closed at the moment. Check back";
    const at = storeIs?.openTomorrowTime?.length ? "at" : "";
    if (storeIs.visitedBeforeOpening) return `${checkBack} at ${storeIs.openTime}.`;
    if (storeIs.visitedAfterClosing) return `${checkBack} ${at} ${storeIs.openTomorrowTime} tomorrow.`;
  };

  const shouldShowSideMenu = !areModalsShown(modals, modalKeys) && sidemenus.sidebar.show;

  return (
    <>
      <nav className="relative">
        {showStoreMessage && (
          <>
            {store.configuration.custom_message && storeIs.open && (
              <div className="bg-white">
                <div className="w-full px-6.25 py-3 flex items-center justify-center bg-primary-500 bg-opacity-20 text-primary-500 text-1xs sm:text-sm font-medium">
                  {store.configuration.custom_message}
                </div>
              </div>
            )}
            {!storeIs.open && (
              <div className="w-full px-6.25 py-3 flex items-center justify-center text-primary-500 bg-primary-500 bg-opacity-10 text-1xs sm:text-sm font-medium">
                {getStoreIsClosedMsgToDisplay()}
              </div>
            )}
          </>
        )}
        <div
          ref={stickyNavRef}
          className={`bg-white stick-to-top ${isSticky ? "fixed top-0 left-0 right-0 shadow-md z-50" : ""}`}
        >
          <div className="">
            <div className="w-full px-5 sm:px-9 md:px-12.5 py-1.75">
              <div className="store-container flex items-center justify-between ">
                <a
                  href={getWhatsappLink(
                    store?.phone,
                    store.configuration.enquiry_message ?? `Hi, i'll like to make an enquiry about ${store.name}`
                  )}
                  className="flex items-center space-x-1 text-black-secondary hover:text-black-muted transition-all ease-out px-2 py-1.5 rounded-[30px] bg-grey-fields-100"
                >
                  {/* prettier-ignore */}
                  <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                  <g clip-path="url(#clip0_2464_7123)">
                    <path d="M0 22L1.55492 16.3536C0.5936 14.6963 0.0893027 12.8194 0.0945558 10.9007C0.0945558 4.88831 5.01146 0 11.0473 0C13.9785 0 16.7311 1.13451 18.7956 3.19439C20.8653 5.25428 22.0052 7.99382 22 10.9059C22 16.9183 17.0831 21.8066 11.042 21.8066H11.0368C9.20343 21.8066 7.40162 21.3465 5.79942 20.4786L0 22ZM6.07784 18.5076L6.40878 18.7063C7.80611 19.5323 9.4083 19.9663 11.042 19.9715H11.0473C16.064 19.9715 20.1509 15.9092 20.1509 10.9111C20.1509 8.4905 19.2053 6.21626 17.4876 4.50143C15.7698 2.7866 13.4795 1.84553 11.0473 1.84553C6.03056 1.8403 1.94365 5.90257 1.94365 10.9007C1.94365 12.6103 2.42168 14.278 3.33572 15.721L3.5511 16.0661L2.6318 19.4068L6.07784 18.5076Z" fill="white"/>
                    <path d="M0.383301 21.6185L1.88569 16.1656C0.955889 14.571 0.46735 12.7568 0.46735 10.9061C0.472603 5.10285 5.21615 0.381836 11.0471 0.381836C13.8785 0.381836 16.5313 1.47975 18.5275 3.46644C20.5237 5.45313 21.6216 8.09857 21.6216 10.9113C21.6216 16.7145 16.8728 21.4355 11.0471 21.4355H11.0418C9.27155 21.4355 7.53277 20.9912 5.98836 20.1547L0.383301 21.6185Z" fill="url(#paint0_linear_2464_7123)"/>
                    <path d="M0 22L1.55492 16.3536C0.5936 14.6963 0.0893027 12.8194 0.0945558 10.9007C0.0945558 4.88831 5.01146 0 11.0473 0C13.9785 0 16.7311 1.13451 18.7956 3.19439C20.8653 5.25428 22.0052 7.99382 22 10.9059C22 16.9183 17.0831 21.8066 11.042 21.8066H11.0368C9.20343 21.8066 7.40162 21.3465 5.79942 20.4786L0 22ZM6.07784 18.5076L6.40878 18.7063C7.80611 19.5323 9.4083 19.9663 11.042 19.9715H11.0473C16.064 19.9715 20.1509 15.9092 20.1509 10.9111C20.1509 8.4905 19.2053 6.21626 17.4876 4.50143C15.7698 2.7866 13.4795 1.84553 11.0473 1.84553C6.03056 1.8403 1.94365 5.90257 1.94365 10.9007C1.94365 12.6103 2.42168 14.278 3.33572 15.721L3.5511 16.0661L2.6318 19.4068L6.07784 18.5076Z" fill="url(#paint1_linear_2464_7123)"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.31054 6.34197C8.10567 5.88712 7.89029 5.87667 7.69593 5.87144C7.53834 5.86621 7.35448 5.86621 7.17062 5.86621C6.98676 5.86621 6.69259 5.93418 6.44044 6.20604C6.18829 6.4779 5.48438 7.13665 5.48438 8.48028C5.48438 9.81868 6.4667 11.1153 6.60329 11.2982C6.73987 11.4812 8.49965 14.3201 11.2785 15.4128C13.5899 16.3225 14.0627 16.1395 14.5617 16.0924C15.0608 16.0454 16.1797 15.4337 16.4108 14.7959C16.6367 14.158 16.6367 13.6143 16.5684 13.4993C16.5001 13.3843 16.3163 13.3163 16.0431 13.1804C15.7699 13.0444 14.4252 12.3857 14.173 12.2916C13.9209 12.2027 13.737 12.1557 13.5584 12.4275C13.3745 12.6994 12.8492 13.3111 12.6916 13.4941C12.534 13.677 12.3712 13.698 12.098 13.562C11.8249 13.4261 10.9423 13.1386 9.89698 12.2079C9.08275 11.4865 8.53117 10.5924 8.37358 10.3206C8.21599 10.0487 8.35782 9.90233 8.4944 9.7664C8.61522 9.64615 8.76756 9.44749 8.90414 9.29064C9.04072 9.1338 9.088 9.01878 9.1773 8.83579C9.26661 8.65281 9.22458 8.49596 9.15629 8.36003C9.088 8.22933 8.55219 6.88047 8.31054 6.34197Z" fill="white"/>
                  </g>
                  <defs>
                    <linearGradient id="paint0_linear_2464_7123" x1="11.0019" y1="21.6175" x2="11.0019" y2="0.381093" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#20B038"/>
                      <stop offset="1" stop-color="#60D66A"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_2464_7123" x1="11.0021" y1="21.9979" x2="11.0021" y2="0" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#F9F9F9"/>
                      <stop offset="1" stop-color="white"/>
                    </linearGradient>
                    <clipPath id="clip0_2464_7123">
                      <rect width="22" height="22" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                  <span className="text-xs md:text-1xs font-medium">Contact Us</span>
                </a>

                <div className="flex items-center space-x-2.5">
                  {currencies.options.length > 0 && (
                    <CurrencySwitcher
                      selected={currencies.active}
                      currencies={currencies.options}
                      setCurrency={changeCurrency}
                      noBg
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="border-y border-grey-border border-opacity-50">
            <div className="w-full px-5 sm:px-9 md:px-12.5 py-3">
              <div className="store-container flex items-center justify-between">
                <button className="flex items-center" onClick={() => toggleSidemenu("sidebar")}>
                  {/* prettier-ignore */}
                  <svg width="24" height="27" viewBox="0 0 24 27" fill="none" >
                  <path d="M3.25 8.33333H22.75" stroke="#3E3E3E" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3.25 13.75H22.75" stroke="#3E3E3E" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3.25 19.1667H22.75" stroke="#3E3E3E" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                </button>

                <button onClick={() => router.push(computedPaths.home)}>
                  <StoreFrontLogo logo={logo} storeName={storeName} />
                </button>

                <button className="relative flex items-center pr-1 py-1" onClick={() => toggleModal("cart")}>
                  {/* prettier-ignore */}
                  <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.9165 7L7.1665 2.66667H20.1665L23.4165 7M3.9165 7V22.1667C3.9165 22.7413 4.14478 23.2924 4.55111 23.6987C4.95743 24.1051 5.50853 24.3333 6.08317 24.3333H21.2498C21.8245 24.3333 22.3756 24.1051 22.7819 23.6987C23.1882 23.2924 23.4165 22.7413 23.4165 22.1667V7M3.9165 7H23.4165M17.9998 11.3333C17.9998 12.4826 17.5433 13.5848 16.7306 14.3975C15.918 15.2101 14.8158 15.6667 13.6665 15.6667C12.5172 15.6667 11.415 15.2101 10.6024 14.3975C9.78972 13.5848 9.33317 12.4826 9.33317 11.3333" stroke="#3E3E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="absolute top-0 right-0 h-4 w-4 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center">
                    <span className="inline-block">{cart && cart.length > 0 ? cart.length : 0}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <MiniSearch
            toggle={() => setShowSearch(!showSearch)}
            show={showSearch}
            storeId={store.id}
            openItem={openItem}
            storeSlug={store.slug}
          />
        </div>
      </nav>

      {allowSearch && (
        <header
          className="w-full new-store-front-hero bg-grey-fields-200" //px-4 sm:px-12 md:px-16 lg:px-20 xl:px-25 py-5 sm:py-7.5
          style={{
            backgroundImage:
              heroImage && showWelcomeMessage
                ? `linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.45)), url(${heroImage})`
                : "none",
          }}
        >
          <div
            className={`w-full block px-5 sm:px-9 md:px-12.5 ${
              !showWelcomeMessage ? "py-7.5 sm:py-8.5 md:py-10" : "py-12.5 sm:py-15 md:py-22.5"
            }`}
          >
            <div className="store-container  space-y-6 sm:space-y-8 md:space-y-15">
              {showWelcomeMessage && (
                <h1
                  className={`${
                    heroImage ? "text-white font-semibold" : "text-black"
                  } text-3lg sm:text-4xl md:text-5xl text-center max-w-xs md:max-w-2xl mx-auto leading-[1.2] sm:leading-[1.4] md:leading-[1.1]`}
                >
                  Hi there! 👋 What are you shopping for today?
                </h1>
              )}
              <div className="max-w-3xl mx-auto">
                <div className={`flex md:flex-none`}>
                  <AppSearchBar
                    toggleAction={() => setShowSearch(!showSearch)}
                    hasToggle
                    {...{
                      placeholder: `Find Products`,
                      searchQuery,
                      setSearchQuery,
                      fullViewOnMobile,
                      setFullViewOnMobile,
                      resetPage,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      <StoreSideNav
        show={shouldShowSideMenu}
        store={store}
        modals={modals}
        toggle={toggle}
        mode={mode}
        formatAsCurrency={formatAsCurrency}
        toggleSidemenu={toggleSidemenu}
      />
    </>
  );
};

export default NewStoreHeader;

export const StoreFrontLogo = ({ logo, storeName }: { logo: string | null; storeName: string }) => {
  return (
    <StoreLogo
      image={logo}
      text={storeName}
      className={classNames(logo ? "h-9 w-9 md" : "text-xl sm:text-xl font-bold pt-1.5")}
    />
  );
};

const modalKeys = [
  "about_store",
  "delivery_fees",
  "more_info",
  "refund_policy",
  "image_details",
  "faq",
  "categories",
  "about_images",
];
